import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { getAirportService } from "@/lib/airportServicesApi";

interface AirportServiceViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceId?: number;
}

const AirportServiceViewModal = ({ open, onOpenChange, serviceId }: AirportServiceViewModalProps) => {
  const { toast } = useToast();
  const [service, setService] = useState<any>(null);

  useEffect(() => {
    if (open && serviceId) {
      loadServiceData();
    }
  }, [open, serviceId]);

  const loadServiceData = async () => {
    if (!serviceId) return;
    
    try {
      const data = await getAirportService(serviceId);
      setService(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatServiceType = (type: string) => {
    const types: Record<string, string> = {
      'arrival': 'Arrival',
      'departure': 'Departure',
      'transit': 'Transit'
    };
    return types[type] || type;
  };

  const formatPricingType = (type: string) => {
    const types: Record<string, string> = {
      'per_person': 'Per Person',
      'per_family': 'Per Family',
      'per_group': 'Per Group'
    };
    return types[type] || type;
  };

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Airport Service Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Badge variant="secondary">{formatServiceType(service.service_type)}</Badge>
            <Badge variant="outline">{formatPricingType(service.pricing_type)}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Service Code</Label>
              <Input value={service.service_code || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Airport</Label>
              <Input value={service.airport || ""} disabled />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Service Name</Label>
            <Input value={service.service_name || ""} disabled />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={service.description || ""} disabled rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Supplier Name</Label>
              <Input value={service.supplier_name || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Cost Supplier</Label>
              <Input value={service.cost_supplier?.toString() || ""} disabled />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Price OP</Label>
              <Input value={service.price_op?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Price AG</Label>
              <Input value={service.price_ag?.toString() || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Price CF</Label>
              <Input value={service.price_cf?.toString() || ""} disabled />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tax Percentage</Label>
            <Input value={service.tax_percentage?.toString() || ""} disabled />
          </div>

          {service.hcte_notes && (
            <div className="space-y-2">
              <Label>HCTE Notes</Label>
              <Textarea value={service.hcte_notes} disabled rows={3} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AirportServiceViewModal;
